import React, { useContext, useEffect, useState } from "react";
import { BrowserRouter, Redirect, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import LoggedInLayout from "../layout";
import Dashboard from "../pages/Dashboard/";
import Tickets from "../pages/Tickets/";
import Signup from "../pages/Signup/";
import Login from "../pages/Login/";
import ForgotPassword from "../pages/ForgotPassword/";
import ResetPassword from "../pages/ResetPassword/";
import Connections from "../pages/Connections/";
import Settings from "../pages/Settings/";
import AiApiLogsPage from "../pages/Settings/AiApiLogsPage";
import Users from "../pages/Users";
import Contacts from "../pages/Contacts/";
import QuickAnswers from "../pages/QuickAnswers/";
import Reminders from "../pages/Reminders/";
import Agenda from "../pages/Agenda/";
import Orders from "../pages/Orders/";
import Processes from "../pages/Processes/";
import Streaming from "../pages/Streaming/";
import Campaigns from "../pages/Campaigns/";
import Webhooks from "../pages/Webhooks/";
import InternalChat from "../pages/InternalChat/";
import Funnels from "../pages/Funnels/";
import Chatbot from "../pages/Chatbot/";
import Ai from "../pages/Ai/";
import SidepanelApps from "../pages/SidepanelApps/";
import WorkspaceData from "../pages/WorkspaceData/";
import Reseller from "../pages/Reseller/";
import ResellerLanding from "../pages/ResellerLanding/";
import Queues from "../pages/Queues/";
import Labels from "../pages/Labels/";
import SuperAdmin from "../pages/SuperAdmin/";
import Monitoring from "../pages/Monitoring/";
import Tutorials from "../pages/Tutorials/";
import Menus from "../pages/Menus/";
import MenuEmbed from "../pages/Menus/Embed";
import { AuthProvider } from "../context/Auth/AuthContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { ConnectionsProvider } from "../context/Connections/ConnectionsContext";
import { ThemeProvider } from "../context/DarkMode";
import Route from "./Route";
import BackdropLoading from "../components/BackdropLoading";
import api from "../services/api";
import { getCachedBranding } from "../utils/branding";

const normalizeEmbedUrl = url => {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return value;
  return "";
};

const LandingEmbed = ({ url }) => (
  <iframe
    src={url}
    title="Landing"
    style={{
      position: "fixed",
      inset: 0,
      width: "100%",
      height: "100%",
      border: 0,
      background: "#fff",
    }}
    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
  />
);

const RootEntry = () => {
  const [checkingDomain, setCheckingDomain] = useState(true);
  const [checkingBranding, setCheckingBranding] = useState(true);
  const [reseller, setReseller] = useState(null);
  const [landingEmbedUrl, setLandingEmbedUrl] = useState(() =>
    normalizeEmbedUrl(getCachedBranding().landingEmbedUrl)
  );

  useEffect(() => {
    let mounted = true;

    api
      .get("/resellers/public", {
        params: { domain: window.location.hostname },
      })
      .then(({ data }) => {
        if (mounted) setReseller(data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (mounted) setCheckingDomain(false);
      });

    api
      .get("/settings/public/branding")
      .then(({ data }) => {
        if (!mounted) return;
        setLandingEmbedUrl(
          normalizeEmbedUrl(data?.landingEmbedUrl)
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (mounted) setCheckingBranding(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (checkingDomain) return <BackdropLoading />;

  if (reseller) return <ResellerLanding initialReseller={reseller} />;

  if (landingEmbedUrl) return <LandingEmbed url={landingEmbedUrl} />;

  if (checkingBranding) return <BackdropLoading />;

  return <Redirect to="/login" />;
};

const DomainAwarePublicPage = ({ component: Component }) => {
  const { isAuth } = useContext(AuthContext);
  const [checkingDomain, setCheckingDomain] = useState(true);
  const [reseller, setReseller] = useState(null);
  const hasStoredToken = Boolean(localStorage.getItem("token"));

  useEffect(() => {
    let mounted = true;

    api
      .get("/resellers/public", {
        params: { domain: window.location.hostname },
      })
      .then(({ data }) => {
        if (mounted) setReseller(data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (mounted) setCheckingDomain(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (checkingDomain) return <BackdropLoading />;

  if (hasStoredToken || isAuth) return <PrivateApp />;

  if (reseller) return <ResellerLanding initialReseller={reseller} />;

  return <Component />;
};

const LoginEntry = () => <DomainAwarePublicPage component={Login} />;
const SignupEntry = () => <DomainAwarePublicPage component={Signup} />;
const ForgotPasswordEntry = () => (
  <DomainAwarePublicPage component={ForgotPassword} />
);
const ResetPasswordEntry = () => (
  <DomainAwarePublicPage component={ResetPassword} />
);

const PrivateApp = () => (
  <ConnectionsProvider>
    <LoggedInLayout>
      <Switch>
        <Route exact path="/" component={Dashboard} isPrivate />
        <Route exact path="/tickets/:ticketId?" component={Tickets} isPrivate />
        <Route exact path="/connections" component={Connections} isPrivate />
        <Route exact path="/contacts" component={Contacts} isPrivate />
        <Route exact path="/users" component={Users} isPrivate />
        <Route exact path="/quickAnswers" component={QuickAnswers} isPrivate />
        <Route exact path="/reminders" component={Reminders} isPrivate />
        <Route exact path="/appointments" component={Agenda} isPrivate />
        <Route exact path="/orders" component={Orders} isPrivate />
        <Route exact path="/processes" component={Processes} isPrivate />
        <Route exact path="/streaming" component={Streaming} isPrivate />
        <Route exact path="/campaigns" component={Campaigns} isPrivate />
        <Route exact path="/webhooks" component={Webhooks} isPrivate />
        <Route exact path="/internal-chat" component={InternalChat} isPrivate />
        <Route exact path="/funnels" component={Funnels} isPrivate />
        <Route exact path="/chatbot" component={Chatbot} isPrivate />
        <Route exact path="/ai" component={Ai} isPrivate />
        <Route exact path="/apps" component={SidepanelApps} isPrivate />
        <Route exact path="/workspace" component={WorkspaceData} isPrivate />
        <Route exact path="/reseller" component={Reseller} isPrivate />
        <Route exact sensitive path="/Settings" component={Settings} isPrivate />
        <Route exact sensitive path="/settings" component={Settings} isPrivate />
        <Route exact path="/settings/ai-logs" component={AiApiLogsPage} isPrivate />
        <Route exact sensitive path="/Queues" component={Queues} isPrivate />
        <Route exact sensitive path="/queues" component={Queues} isPrivate />
        <Route exact path="/labels" component={Labels} isPrivate />
        <Route exact path="/superadmin" component={SuperAdmin} isPrivate />
        <Route exact path="/monitoring" component={Monitoring} isPrivate />
        <Route exact path="/tutorials" component={Tutorials} isPrivate />
        <Route exact path="/menu" component={Menus} isPrivate />
        <Route exact path="/menu-embed/:itemId" component={MenuEmbed} isPrivate />
      </Switch>
    </LoggedInLayout>
  </ConnectionsProvider>
);

const AppSwitch = () => {
  const { isAuth } = useContext(AuthContext);
  const hasStoredToken = Boolean(localStorage.getItem("token"));

  return (
    <Switch>
      <Route exact path="/login" component={LoginEntry} />
      <Route exact path="/signup" component={SignupEntry} />
      <Route exact path="/forgot-password" component={ForgotPasswordEntry} />
      <Route exact path="/reset-password" component={ResetPasswordEntry} />
      <Route exact path="/r/:slug?" component={ResellerLanding} allowAuthenticated />
      {!hasStoredToken && !isAuth && (
        <Route exact path="/" component={RootEntry} allowAuthenticated />
      )}
      <Route path="/" component={PrivateApp} isPrivate skipPermission />
    </Switch>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <AppSwitch />
          <ToastContainer autoClose={3000} />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;

